import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { HELP_PHONEBOOKS } from "@/constants/HelpUrls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import FabBar from "@/design/components/FabBar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viGear, viIdCard, viPlus } from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import ContactCard from "./ContactCard";
import contactGroupReducer from "./contactGroupReducer";
import ImportPhonebook from "./ImportPhonebook";

function ContactGroup() {
  const { pbxId, id, groupId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pbx = useSelector((state) => state.domain.pbx);

  const [phonebook, setPhonebook] = useState({});
  const [tab, setTab] = useState(() => (groupId === "new" ? 1 : 0));
  const [previousGroupId, setPreviousGroupId] = useState(groupId);

  if (groupId !== previousGroupId) {
    setPreviousGroupId(groupId);
    setTab(groupId === "new" ? 1 : 0);
  }
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_PHONEBOOKS));
  }, [dispatch]);

  const handleTab = (event, newTab) => setTab(newTab);

  const {
    value: contactGroup,
    dispatch: contactGroupDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: { id: null, units: [] },
    reducer: contactGroupReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
    }),
    onSubmit: () => Api.storePhonebookGroup(phonebook, contactGroup),
    onSuccess: (response) => {
      if (!contactGroup.id) {
        contactGroupDispatch({ type: "id", value: response.data.phonebookGroup.id });
        navigate(`/pbx/${pbxId}/phonebooks/${phonebook.id}/${response.data.phonebookGroup.id}`);
      }
    },
  });

  const handleChangePage = (event, page) => {
    toggleLoadingState(true);

    Promise.resolve(Api.getPhonebookGroup({ id }, { id: groupId }, page - 1))
      .then((response) => {
        contactGroupDispatch({ type: "set", value: response.data.phonebookGroup });
      })
      .finally(() => {
        toggleLoadingState(false);
        setPage(page - 1);
      });
  };

  // Fetch the holiday set, but only if an ID is given
  useEffect(() => {
    toggleLoadingState(true);

    if (groupId !== "new") {
      Promise.all([Api.getPhonebook({ id }), Api.getPhonebookGroup({ id }, { id: groupId }, 0)])
        .then((_response) => {
          setPhonebook(_response[0].data.phonebook);
          contactGroupDispatch({ type: "set", value: _response[1].data.phonebookGroup });
        })
        .finally(toggleLoadingState);
    } else {
      Promise.resolve(Api.getPhonebook({ id }))
        .then((_response) => setPhonebook(_response.data.phonebook))
        .finally(toggleLoadingState);
    }
  }, [contactGroupDispatch, groupId, id]);

  function refreshPhonebookData() {
    Promise.resolve(Api.getPhonebookGroup({ id }, { id: contactGroup.id }, 0))
      .then((response) => contactGroupDispatch({ type: "set", value: response.data.phonebookGroup }))
      .finally(() => setPage(0));
  }

  // Handle changes
  const formHandler = (property, value) => {
    contactGroupDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  function addContact() {
    contactGroupDispatch({ type: "addContact" });
  }

  // Delete
  function onDeleteFinish() {
    navigate(`/pbx/${pbxId}/phonebooks/${id}`);
  }

  return (
    <Page maxWidth="xl">
      <title>{`${contactGroup.name || "Group"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "Contacts", icon: viIdCard, id: "contacts", disabled: !contactGroup.id },
            { label: "Settings", icon: viGear, id: "settings" },
          ]}
          avatar={
            <IconButton variant="avatar" component={NavLink} to={`/pbx/${pbxId}/phonebooks/${id}`}>
              <Valkyrie icon={viArrowLeft} />
              <Avatar text={contactGroup.name} icon={viIdCard} />
            </IconButton>
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={contactGroup.id || "nk"}
                isActionable={!!contactGroup?.id}
                onDelete={() => Api.deletePhonebookGroup(phonebook, contactGroup)}
                name={contactGroup.name}
                deleteInfo="Are you sure you want to delete this contact group?"
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={contactGroup.name || "Unnamed contact group"}
          subheader={phonebook.name || "Phonebook"}
        />
        {tab === 0 && (
          <>
            <Box sx={{ pb: (contactGroup?.unitCount ? Math.ceil(contactGroup?.unitCount / 50) : 0) > 1 ? 6.5 : 0 }}>
              <FormGroup
                title="Contacts"
                actions={
                  <Button variant="contained" startIcon={<Valkyrie icon={viPlus} />} onClick={addContact}>
                    New contact
                  </Button>
                }
              >
                <Grid size={12}>
                  <TableContainer sx={{ m: -2, width: "calc(100% + 32px)" }}>
                    <Table sx={{ minWidth: 936 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">#</TableCell>
                          <TableCell style={{ minWidth: 180, width: "25%" }}>Name</TableCell>
                          <TableCell style={{ minWidth: 120, width: "20%" }}>Company</TableCell>
                          <TableCell style={{ minWidth: 140 }}>Office</TableCell>
                          <TableCell style={{ minWidth: 140 }}>Work</TableCell>
                          <TableCell style={{ minWidth: 140 }}>Personal</TableCell>
                          <TableCell style={{ width: 112 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contactGroup.units.length >= 1 ? (
                          contactGroup.units.map((contact, key) => (
                            <ContactCard
                              key={key + 1 + page * 50}
                              contact={contact}
                              location={key + 1 + page * 50}
                              phonebook={{ id: phonebook.id }}
                              contactGroup={contactGroup}
                            />
                          ))
                        ) : (
                          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                            <TableCell component="th" scope="row" colSpan={7}>
                              There are no contacts in this group.
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell component="th" scope="row" colSpan={7}>
                            <Button variant="contained" startIcon={<Valkyrie icon={viPlus} />} onClick={addContact}>
                              New contact
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </FormGroup>
            </Box>
            {(contactGroup?.unitCount ? Math.ceil(contactGroup?.unitCount / 50) : 0) > 1 && (
              <FabBar>
                <Pagination
                  count={contactGroup?.unitCount ? Math.ceil(contactGroup?.unitCount / 50) : 0}
                  page={page + 1}
                  onChange={handleChangePage}
                  style={{ zIndex: 1000 }}
                />
              </FabBar>
            )}
          </>
        )}
        {tab === 1 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="name"
                  label="Name"
                  onChange={formHandler}
                  value={contactGroup.name}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            {contactGroup.id && (
              <ImportPhonebook
                phonebook={phonebook}
                refreshPhonebookData={refreshPhonebookData}
                contactGroup={contactGroup}
              />
            )}
          </>
        )}
      </Stack>
    </Page>
  );
}

export default ContactGroup;
